import { useEffect } from "react";

// Makes a failed submission impossible to miss. Every form already renders its
// own errors inline, as `role="alert"` elements beside the field or above the
// button — but on a long page (a program's rules, settings) the one that matters
// can sit a screen away from the button that was pressed, and the page looked
// like it did nothing.
//
// So after a failure this finds the error the page just rendered, scrolls it into
// view, and raises a toast carrying its text. It reads the DOM rather than the
// error object on purpose: the inline copy is already the page's own words in the
// reader's language, while an `ApiRequestError`'s `.message` is `types/errors.ts`'s
// English. One mechanism, and no form has to opt in — `apiFetch` calls
// `announceFailure` on a failed write, and the entrypoint calls it on an Inertia
// visit that came back with validation errors.
//
// The contract a form keeps is only this: an error it shows is `role="alert"`.

export type Toast = { id: number; message: string | null; target: HTMLElement | null };

type Listener = (toast: Toast | null) => void;

const listeners = new Set<Listener>();
let nextId = 1;
let quietHolders = 0;

export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function show(toast: Toast | null): void {
  listeners.forEach((listener) => listener(toast));
}

export function dismissToast(): void {
  show(null);
}

// The till opts out. Its errors already take the middle of a screen that does not
// scroll, a failed scan is routine there rather than a surprise, and a toast over
// the camera would cover the next customer's code.
export function useQuietErrorFeedback(): void {
  useEffect(() => {
    quietHolders += 1;
    return () => {
      quietHolders -= 1;
    };
  }, []);
}

const ALERT_SELECTOR = '[role="alert"]';
// How long to wait for the page to render the error after the request failed. The
// caller's `catch` sets state and React commits on its own schedule, so the
// element is looked for over a few frames rather than assumed to be there.
const SEARCH_FRAMES = 20;

function visibleAlerts(): HTMLElement[] {
  return Array.from(document.querySelectorAll<HTMLElement>(ALERT_SELECTOR)).filter(
    (element) => element.getClientRects().length > 0 && (element.textContent ?? "").trim() !== "",
  );
}

// Called at the moment of failure, before the page has reacted: each alert already
// on screen and what it said. An alert that is new, or now says something
// different, is the one this failure produced — which keeps a standing notice
// higher up the page (the card's "wallet unavailable") from being mistaken for it.
function snapshot(): Map<HTMLElement, string> {
  return new Map(visibleAlerts().map((element) => [element, element.textContent ?? ""]));
}

export function announceFailure(): void {
  if (quietHolders > 0 || typeof window === "undefined") return;

  const before = snapshot();
  let frames = 0;

  const look = () => {
    const alerts = visibleAlerts();
    const fresh = alerts.find((element) => before.get(element) !== element.textContent);
    if (fresh) return reveal(fresh);
    if (++frames < SEARCH_FRAMES) return void window.requestAnimationFrame(look);
    // Nothing new appeared: the same error re-rendered unchanged, or the page shows
    // none. Point at the first one there is, or say something went wrong.
    reveal(alerts[0] ?? null);
  };

  window.requestAnimationFrame(look);
}

function reveal(target: HTMLElement | null): void {
  if (target) scrollToError(target);
  show({ id: nextId++, message: target?.textContent?.trim() ?? null, target });
}

// Only when it is actually out of sight — an error already on screen stays put
// rather than the page lurching under the reader. Centred, so the phone's sticky
// top bar never covers it.
export function scrollToError(target: HTMLElement): void {
  if (!target.isConnected) return;
  const rect = target.getBoundingClientRect();
  const inView = rect.top >= 64 && rect.bottom <= window.innerHeight;
  if (inView) return;

  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  target.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
}
